import React, {
  useContext,
  useEffect,
  useMemo,
  lazy,
  useState,
  useRef,
  useCallback,
} from "react"
import { Image, Typography, Badge } from "antd"

import { useHistory, useLocation } from "react-router"
import useNotification from "../../hooks/useNotification"
import { AuthContext } from "../../contexts/auth/AuthContext"
import {
  onOrderCreateArray_onOrderCreateArray,
} from "../../pages/orders/types/onOrderCreateArray"
import DynamicFromNow from "../../components/DynamicFromNow"
import moment from "moment"
import useQueryParams from "../../hooks/useQueryParams"
import useSettings from "../../hooks/useSettings"
import { toast } from "react-toastify"
import Drawer from "../../components/drawer"

import { GET_COUNTERPARTY } from "./../../pages/counterparties/queries"
import { GET_MUSICS } from "../../rest-api/queries/music"
import { useQuery as useQueryR } from "@tanstack/react-query"
import { SocketContext } from "../../contexts/SocketContext"
import { MEDIA_URL } from "../../utils/config"
import hasPermission from "../../utils/hasPermission"
import { soundEngine } from "../../utils/soundEngine"
import i18n from "../../services/i18n"

const OrdersNotification = lazy(() => import("./OrdersNotification"))

const audioSrc = require("../../assets/juntos.mp3")

const resolveAudioUrl = (path?: string | null) => {
  if (!path) return ""
  if (
    path.startsWith("http://") ||
    path.startsWith("https://") ||
    path.startsWith("blob:") ||
    path.startsWith("data:")
  )
    return path
  if (path.startsWith("/var/www/crm/media/")) return `${MEDIA_URL}${path.slice(19)}`
  if (path.startsWith("/media/")) return `${MEDIA_URL}${path.slice(7)}`
  if (path.startsWith("media/")) return `${MEDIA_URL}${path.slice(6)}`
  if (path.startsWith("/")) return `${MEDIA_URL}${path.slice(1)}`
  return `${MEDIA_URL}${path}`
}

export default function NotificationsController({
  children,
}: {
  children: JSX.Element
}) {
  const { authState } = useContext(AuthContext)
  const { connectionStatus, lastMessage, setUnreadChat } = useContext(SocketContext)

  const { data: musics } = useQueryR(["GET_MUSICS"], GET_MUSICS, { retry: false })
  const activeOrderAudioRef = useRef<HTMLAudioElement | null>(null)

  const { data: counterpartyData } = useQueryR(
    ["COUNTERPARTY", lastMessage ? lastMessage.Data?.CounterpartyId : 1],
    () => GET_COUNTERPARTY(lastMessage ? lastMessage.Data?.CounterpartyId : 1),
    {
      enabled: false,
    }
  )

  const history = useHistory()
  const location = useLocation()
  const query = useQueryParams()
  const { notify } = useNotification()
  const [settings] = useSettings()
  const isClient = !!authState.user?.counterpartyId

  const playNotificationSound = useCallback(
    (isOrderLoop = false) => {
      let storedMusicId: number | undefined
      try {
        storedMusicId = JSON.parse(localStorage.getItem("user") || "{}").musicId
      } catch {
        storedMusicId = undefined // buzilgan localStorage ilovani yiqitmasin
      }

      // ⛔ Boshliq (2026-09-03): «sozlamalarda ovozsiz turibdi, ammo ovoz chiqmoqda».
      // Sabab shu yerda edi: avval `authState?.user?.musicId ?? storedMusicId` yozilgan.
      // AudioChange «ovozsiz» tanlanganda localStorage'ga 0 yozadi va serverga yuboradi,
      // lekin authState'ni YANGILAMAYDI. `??` esa faqat null/undefined bo'lsa keyingisiga
      // o'tadi — ya'ni authState'dagi ESKI qiymat (masalan 1) ustun kelib, yangi saqlangan
      // 0 e'tiborsiz qolardi va ovoz chiqaverardi.
      // Endi localStorage BIRINCHI: u tanlov paytida darhol yoziladi, ya'ni eng yangi manba.
      const userMusicId = storedMusicId ?? authState?.user?.musicId

      // 0 = ovozsiz
      if (userMusicId === 0) {
        return null
      }

      // Boshliq (2026-09-05): standart ovoz — Modern Ping (5), Telegram Chime emas
      const activeSoundId = userMusicId || 5
      soundEngine.playSound(activeSoundId)
      return null
    },
    [authState?.user?.musicId]
  )

  const orderToastFunc = (
    order: onOrderCreateArray_onOrderCreateArray | null
  ) => {
    if (activeOrderAudioRef.current) {
      activeOrderAudioRef.current.pause()
      activeOrderAudioRef.current.currentTime = 0
      activeOrderAudioRef.current = null
    }
    // tog'a #1914 (2026-09-14): Orders/UpdateOrderMessageStatus endpointi 2026-09-02 da olib tashlangan (yagona Chat API) — chaqiruv 405 berardi
  }

  // Yagona chat (egasi 2026-09-02): «chat» socket hodisasi — toast + o'qilmagan belgisi (matn va kartalar)
  useEffect(() => {
    if (lastMessage && lastMessage?.nameFunction === "chat" && lastMessage?.typeFunction === "create") {
      if (!hasPermission(authState?.user, "/chat")) return
      const data = lastMessage?.data
      const counterpartyId = data?.counterpartyId
      const uTenantIdC = Number((authState?.user as any)?.tenantId || 1)
      let targetId = counterpartyId
      if (uTenantIdC > 1) {
        const linkedCtC = (authState?.user as any)?.linkedCounterpartyId
        if (linkedCtC && Number(linkedCtC) === Number(counterpartyId)) targetId = -1
        else if (!(authState?.user as any)?.counterpartyId && String(data?.tenantId) !== String(uTenantIdC)) return
      }
      if (`/chat/${targetId}` === location.pathname) return
      if (isClient && authState.user?.counterpartyId !== counterpartyId) return
      const currentUserId = authState?.user?.id || (authState?.user as any)?.userId
      const msgUserId = data?.userId ?? data?.UserId
      if (currentUserId && msgUserId && currentUserId === msgUserId) return
      if (settings?.notifications?.moneyChat ?? true) playNotificationSound()
      if (setUnreadChat) setUnreadChat(true)
      const senderEmail = data?.kind === "event" ? i18n.t("chat") : (data?.user?.userName || data?.user?.email || i18n.t("messageLbl"))
      const body = data?.commentsText || (data?.photo ? i18n.t("photoTag") : data?.voiseMessage ? i18n.t("voiceMessageTag") : "")
      toast(
        <>
          <Typography.Text strong>{senderEmail}</Typography.Text>{" "}
          <div style={{ marginTop: 4 }}>{body}</div>
        </>,
        { type: "info", toastId: `chat${data?.id?.toString()}` || "", autoClose: 6000, closeOnClick: true, onClick: () => { history.push(`/chat/${targetId}`) } }
      )
      notify(senderEmail, { body })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage, playNotificationSound, settings?.notifications?.moneyChat])


  useEffect(() => {
    if (
      lastMessage &&
      lastMessage?.nameFunction === "factoryPurchase"
    ) {
      const data = lastMessage?.data
      const typeFunc = lastMessage?.typeFunction

      playNotificationSound()

      const content = (
        <div>
          <Typography.Text strong style={{ color: "#fa8c16" }}>
            🏭 Закупка завода
          </Typography.Text>
          <div style={{ marginTop: 4 }}>
            {data?.Message || `Поступила новая закупка завода №${data?.Document}, требуется подтверждение.`}
          </div>
        </div>
      )

      toast(content, {
        type: typeFunc === "confirm" ? "success" : "warning",
        toastId: `factoryPurchase_${data?.Document}_${typeFunc}` || "",
        autoClose: 8000,
        closeOnClick: true,
        onClick: () => {
          // AUDIO-23 (2026-09-08): hamkor/magazin — o'z Закупки sahifasi (kutilayotgan hujjatlar paneli); glavniy — Закупка завода
          history.push(Number((authState?.user as any)?.tenantId || 0) > 1 ? "/purchases" : "/factoryPurchases")
        },
      })

      notify(i18n.t("factoryPurchase"), {
        body: data?.Message || `Документ №${data?.Document}`,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage, playNotificationSound])

  // talabnoma §2.2 (2026-09-06): hamkor sotuv hujjatini tasdiqladi/rad etdi → sotuvchi menejerga jonli xabar (CONFIRMED/REJECTED)
  useEffect(() => {
    if (lastMessage && lastMessage?.nameFunction === "sale") {
      const data = lastMessage?.data
      const typeFunc = lastMessage?.typeFunction
      const rejected = typeFunc === "reject" || /отклон|rad/i.test(String(data?.Message || ""))
      playNotificationSound()
      toast(
        <div>
          <Typography.Text strong style={{ color: rejected ? "#dc2626" : "#16a34a" }}>{rejected ? "❌ Продажа отклонена" : "✅ Продажа подтверждена"}</Typography.Text>
          <div style={{ marginTop: 4 }}>{data?.Message || `Документ №${data?.Document}`}</div>
        </div>,
        { type: rejected ? "error" : "success", toastId: `sale_${data?.Document}_${typeFunc}_${data?.Date}`, autoClose: 8000, closeOnClick: true, onClick: () => history.push("/sales") }
      )
      notify(rejected ? "Продажа отклонена" : "Продажа подтверждена", { body: data?.Message || `Документ №${data?.Document}` })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage, playNotificationSound])

  // Boshliq (2026-09-03, 8-topshiriq): glavniy qaytarish/brak qilganda hamkor tizimiga «Возврат закупки»
  // ko'prik orqali tushadi — ovoz + toast (factoryPurchase bilan bir xil naqsh)
  useEffect(() => {
    if (lastMessage && lastMessage?.nameFunction === "returnPurchase") {
      const data = lastMessage?.data
      playNotificationSound()
      const content = (
        <div>
          <Typography.Text strong style={{ color: "#d4380d" }}>
            ↩️ Возврат поставщику
          </Typography.Text>
          <div style={{ marginTop: 4 }}>
            {data?.Message || `Возврат по закупке №${data?.Document}`}
          </div>
        </div>
      )
      toast(content, {
        type: "info",
        toastId: `returnPurchase_${data?.Document}` || "",
        autoClose: 8000,
        closeOnClick: true,
        onClick: () => { history.push("/returnPurchases") },
      })
      notify("Возврат поставщику", { body: data?.Message || `Документ №${data?.Document}` })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage, playNotificationSound])

  // Boshliq (2026-09-04, real vaqt): rol-huquq o'zgarsa server WS 'permissions/update' yuboradi — huquqlar darhol
  // qayta o'qiladi (AuthContext 'crm-refresh-perms'), menyu/route'lar 1–2 s ichida yangilanadi (qayta kirish shart emas)
  // talabnoma §10 (2026-09-06): server endi faqat tegishli userlarga yuboradi; bu yerda takror kadr (BulkUpdate + UpdateRoleColumns
  // ketma-ket) bir marta o'qiladi va 0–500 ms jitter bilan — bir rolning yuzlab sessiyasi MyPermissions'ga bir zumda urmasin
  const lastPermKey = useRef("")
  useEffect(() => {
    if (lastMessage && lastMessage?.nameFunction === "permissions") {
      const key = JSON.stringify(lastMessage?.data || {})
      if (key && key === lastPermKey.current) return
      lastPermKey.current = key
      const timer = setTimeout(() => { try { window.dispatchEvent(new Event("crm-refresh-perms")) } catch {} }, Math.floor(Math.random() * 500))
      return () => clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage])

  // tog'a 1040 (2026-09-04): hamkor qaytarish qilganda sotuvchiga «Возврат от партнёра» (teskari ko'prik)
  useEffect(() => {
    if (lastMessage && lastMessage?.nameFunction === "returnSale") {
      const data = lastMessage?.data
      playNotificationSound()
      toast(
        <div>
          <Typography.Text strong style={{ color: "#d4380d" }}>↩️ Возврат от партнёра</Typography.Text>
          <div style={{ marginTop: 4 }}>{data?.Message || `Документ №${data?.Document}`}</div>
        </div>,
        { type: "info", toastId: `returnSale_${data?.Document}` || "", autoClose: 8000, closeOnClick: true, onClick: () => { history.push("/returnSales") } }
      )
      notify("Возврат от партнёра", { body: data?.Message || `Документ №${data?.Document}` })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage, playNotificationSound])

  // tog'a #2455 (2026-09-24): PEREVOD — jonli bildirishnoma. Backend (TransferEvents) «transfer» yuboradi,
  // lekin handler yo'q edi: qo'ng'iroqcha faqat 3 daqiqalik so'rovda yangilanardi
  useEffect(() => {
    if (lastMessage && lastMessage?.nameFunction === "transfer") {
      const data = lastMessage?.data
      playNotificationSound()
      toast(
        <div>
          <Typography.Text strong style={{ color: "#2563eb" }}>📦 {data?.Title || "Перемещение"}</Typography.Text>
          <div style={{ marginTop: 4 }}>{data?.Message || ""}</div>
        </div>,
        { type: "info", toastId: `transfer_${data?.RefId || ""}_${lastMessage?.typeFunction || ""}`, autoClose: 10000, closeOnClick: true, onClick: () => { history.push("/transfers") } }
      )
      notify(data?.Title || "Перемещение", { body: data?.Message || "" })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage, playNotificationSound])

  // tog'a 1796 (2026-09-13): PUL ko'prigi — jonli bildirishnoma. Backend Ws(tid,"money",...) yuborardi
  // (ko'zgu yaratildi / tasdiqlandi / rad etildi), lekin frontendda handler YO'Q edi: toast ham, ovoz ham
  // chiqmasdi — foydalanuvchi faqat 60 sekundlik polling yoki qo'ng'iroqcha ro'yxatidan bilardi
  useEffect(() => {
    if (lastMessage && lastMessage?.nameFunction === "money") {
      const data = lastMessage?.data
      playNotificationSound()
      toast(
        <div>
          <Typography.Text strong style={{ color: "#16a34a" }}>💵 Платёж — требуется внимание</Typography.Text>
          <div style={{ marginTop: 4 }}>{data?.Message || `Документ №${data?.Document || ""}`}</div>
        </div>,
        { type: "info", toastId: `money_${data?.Document || ""}`, autoClose: 8000, closeOnClick: true, onClick: () => { history.push("/moneys") } }
      )
      notify("Платёж", { body: data?.Message || `Документ №${data?.Document || ""}` })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage, playNotificationSound])

  // tog'a #3916 (2026-10-08): «registratsiya uchun zayavka tashlasa ham notification kelsin ovozli». Backend (PartnerApplications.NotifyNew)
  // ko'rib chiquvchilarga 🔔 yozadi va «partner_application / new» yuboradi, lekin bu yerda handler YO'Q edi: ovoz ham, toast ham chiqmasdi —
  // qo'ng'iroqcha faqat so'rov bilan yangilanardi. Server xabarni faqat ko'rib chiquvchilarga yuboradi (arizachi ma'lumoti boshqalarga tarqalmaydi).
  useEffect(() => {
    if (lastMessage && lastMessage?.nameFunction === "partner_application" && lastMessage?.typeFunction === "new") {
      const data = lastMessage?.data
      const id = Number(data?.RefId || 0)
      playNotificationSound()
      toast(
        <div>
          <Typography.Text strong style={{ color: "#2563eb" }}>📝 {data?.Title || "Новая заявка партнёра"}</Typography.Text>
          <div style={{ marginTop: 4 }}>{data?.Message || ""}</div>
        </div>,
        { type: "info", toastId: `partner_application_${id}`, autoClose: 12000, closeOnClick: true, onClick: () => { history.push(id > 0 ? `/partnerApplications?id=${id}` : "/partnerApplications") } }
      )
      notify(data?.Title || "Новая заявка партнёра", { body: data?.Message || "" })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage, playNotificationSound])

  useEffect(() => {
    if (
      lastMessage &&
      lastMessage?.nameFunction === "orders" &&
      lastMessage?.typeFunction === "update"
    ) {
      const order = lastMessage?.data
      // toga #2067 (2026-09-14): hamkor tizimida (tenant > 1) bu xabar SAV «yangi zakazlar» oynasi uchun emas —
      // avval bosilganda SAV parametrlari joriy sahifaga (ko'pincha bosh panel) qo'shilardi. Endi «Заказ поставщику» shu hujjat bilan.
      const meTid = (() => { try { return Number(JSON.parse(localStorage.getItem("user") || "{}")?.tenantId || 1) } catch { return 1 } })()
      if (meTid > 1) {
        const doc = String((order as any)?.Document || (order as any)?.document || "")
        toast((order as any)?.Message || `Заказ ${doc}`, { type: "info", toastId: `partner_order_${doc}`, autoClose: 10000, closeOnClick: true,
          onClick: () => { history.push("/orderToMain" + (doc ? `?search=${encodeURIComponent(doc)}` : "")) } })
        return
      }
      let orders: any[] = []

      order && orders.push(order)

      orders = orders.filter(
        (x) => x?.counterpartyId !== +query.secureGet("counterpartyId")
      )

      if (
        !orders.length ||
        orders.every(
          (x) => x?.counterpartyId === +query.secureGet("counterpartyId")
        )
      )
        return

      if (settings?.notifications?.order ?? true) {
        if (activeOrderAudioRef.current) {
          activeOrderAudioRef.current.pause()
          activeOrderAudioRef.current.currentTime = 0
        }
        activeOrderAudioRef.current = playNotificationSound(true)
      }

      orders.forEach((order) => {
        const content = (
          <div>
            <Typography.Text strong style={{ display: "block" }}>
              {order?.mainCounterparty?.Email}
            </Typography.Text>
            <DynamicFromNow
              contentStyle={{ display: "block" }}
              date={moment().toISOString()}
              content={`${order?.mainProduct?.nameModel} - ${order?.mainProduct?.article}`}
            />
          </div>
        )
        toast.isActive(`counterparty${order?.counterpartyId?.toString()}` || "")
          ? toast.update(
              `counterparty${order?.counterpartyId?.toString()}` || "",
              {
                render: content,
              }
            )
          : showOrderNotification(order, content)
      })
    }
    function showOrderNotification(
      order: onOrderCreateArray_onOrderCreateArray | null,
      content: JSX.Element
    ) {
      notify(order?.mainCounterparty?.Email, {})
      toast(content, {
        type: "info",
        toastId: `counterparty${order?.counterpartyId?.toString()}` || "",
        autoClose: false,
        closeButton: true,
        onClose: () => orderToastFunc(order),
        onClick: () => {
          orderToastFunc(order)
          query.append({
            newOrders: "true",
            newOrderCounterpartyId: order?.counterpartyId?.toString() || "",
          })
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage, counterpartyData, playNotificationSound, settings?.notifications?.order])

  useEffect(() => {
    if (
      lastMessage &&
      lastMessage?.nameFunction === "orders" &&
      lastMessage?.typeFunction === "update"
    ) {
      if (lastMessage?.data?.messageStatus === 0) {
        toast.dismiss(
          `counterparty${lastMessage?.data?.counterpartyId?.toString()}`
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage])

  return (
    <>
      {children}
      <Drawer
        removePaddingX
        placement={"left"}
        className="ordersNotificationDrawer"
        width="100%"
        matcher="newOrders"
        removeParamsOnClose={["newOrderCounterpartyId", "ordersterm"]}
      >
        <OrdersNotification />
      </Drawer>
    </>
  )
}
